// checkout/steps/paymentMethodListeners.ts

import { submitFormAndUpdateStep } from '../utils/submitForm';
import { checkoutData } from '../init';
import { registerStepFocusCallback } from '../utils/stepFocusCallbacks';
import {
	attachBillingToggle,
	BILLING_CHANGE_EVENT,
	billingSubmitListener,
	cancelBillingEditIfOpen,
	initialiseBillingSection,
	showBillingAddress,
} from './billingAddressSection';
//import { toggleClass } from '../../utils/toggleClass';

declare global {
	interface Window {
		braintreeFunctions?: {
			showBraintreeHostedFields: (trigger: EventTarget, show: boolean, url: string) => void;
			doesBrowserSupportGooglePay: () => Promise<boolean>;
			doesBrowserSupportApplePay: () => Promise<boolean>;
			initialisePayPalButton: (token: string, containerId: string) => Promise<void>;
			initialiseGooglePayButton: (token: string, containerId: string) => Promise<void>;
			initialiseApplePayButton: (token: string, containerId: string) => Promise<void>;
		};
	}
}

/**
 * Adds listeners to the payment method form.
 * Handles payment selection, Braintree toggling, and form submission.
 * Also attaches the billing address section/reveal.
 *
 * @returns {void}
 */
export function addPaymentMethodListeners(): void {
	const form = document.getElementById('paymentMethodForm');
	if (!(form instanceof HTMLFormElement)) return;
	const f: HTMLFormElement = form;

	const group = document.getElementById('payment-method-options') as HTMLElement | null;
	const braintreeWrapper = document.getElementById('checkout-payment-braintree-wrapper');
	const continueBtn = document.getElementById('checkout-continue-payment-details');
	const errorEl = document.getElementById('paymentMethod-error');

	let hostedFieldsInit = false;
	let hostedFieldsInitPromise: Promise<void> | null = null;

	/**
	 * Initialise Braintree Hosted Fields once.
	 * @param {EventTarget} trigger - Element that triggered initialisation.
	 * @returns {void}
	 */
	function initHostedFieldsOnce(trigger: EventTarget): void {
		if (hostedFieldsInit || hostedFieldsInitPromise) return;
		const braintreeUrl = checkoutData.paymentMethod?.braintreeUrl;
		if (
			!window.braintreeFunctions?.showBraintreeHostedFields ||
			typeof braintreeUrl !== 'string'
		)
			return;
		const fullUrl = braintreeUrl + encodeURIComponent(window.location.toString());
		hostedFieldsInitPromise = Promise.resolve()
			.then(() => {
				window.braintreeFunctions!.showBraintreeHostedFields(trigger, true, fullUrl);
				hostedFieldsInit = true;
			})
			.finally(() => {
				hostedFieldsInitPromise = null;
			});
	}

	/** @returns {void} */
	function updateContinueState(): void {
		const hasSelection = !!f.querySelector<HTMLInputElement>(
			'input[name="paymentMethod"]:checked',
		);
		if (hasSelection) continueBtn?.removeAttribute('disabled');
		else continueBtn?.setAttribute('disabled', 'true');
	}

	/**
	 * Update aria-checked/aria-disabled on payment labels.
	 * @param {ParentNode} [root=f]
	 * @returns {void}
	 */
	function updateAriaChecked(root: ParentNode = f): void {
		const labels = root.querySelectorAll<HTMLLabelElement>('label.payment__option--label[for]');
		labels.forEach((label) => {
			const forId = label.getAttribute('for')!;
			const input =
				(label.control as HTMLInputElement | null) ??
				root.querySelector<HTMLInputElement>('#' + forId);
			if (!input) return;
			label.setAttribute('aria-checked', input.checked ? 'true' : 'false');
			if (input.disabled) label.setAttribute('aria-disabled', 'true');
			else label.removeAttribute('aria-disabled');
		});
	}

	/**
	 * Programmatically select a payment option by its label.
	 * @param {HTMLLabelElement} label
	 * @returns {void}
	 */
	function selectByLabel(label: HTMLLabelElement): void {
		const forId = label.getAttribute('for');
		if (!forId) return;
		const input =
			(label.control as HTMLInputElement | null) ??
			f.querySelector<HTMLInputElement>('#' + forId);
		if (!input || input.disabled) return;

		if (!input.checked) {
			input.checked = true;
			input.dispatchEvent(new Event('change', { bubbles: true }));
		}
		label.focus();
	}

	/**
	 * Toggle UI based on selected payment option.
	 * @param {HTMLInputElement} option
	 * @returns {void}
	 */
	function handlePaymentSelection(option: HTMLInputElement): void {
		cancelBillingEditIfOpen();
		const isBraintree = option.id === 'new-payment-method-braintree-card';
		if (isBraintree) {
			continueBtn?.setAttribute('hidden', 'true');
			braintreeWrapper?.removeAttribute('hidden');
			initHostedFieldsOnce(option);
		} else {
			continueBtn?.removeAttribute('hidden');
			continueBtn?.removeAttribute('disabled');
			braintreeWrapper?.setAttribute('hidden', 'true');
		}
	}

	/**
	 * Uncheck the payment option
	 */
	function resetPaymentSelection(): void {
		// uncheck any selected radio
		const checked = f.querySelector<HTMLInputElement>('input[name="paymentMethod"]:checked');
		if (checked) {
			checked.checked = false;
		}

		// ensure Braintree UI is collapsed and normal continue is visible
		braintreeWrapper?.setAttribute('hidden', 'true');
		continueBtn?.removeAttribute('hidden');

		// clear any payment error shown
		if (errorEl) errorEl.hidden = true;

		// refresh ARIA and button state
		updateAriaChecked();
		updateContinueState();
	}

	// when “Change billing” is opened, reset selection
	document.addEventListener(BILLING_CHANGE_EVENT, resetPaymentSelection);

	// Change handler for native radios
	f.addEventListener('change', (e) => {
		const target = e.target as HTMLElement | null;
		if (
			target instanceof HTMLInputElement &&
			target.type === 'radio' &&
			target.name === 'paymentMethod'
		) {
			handlePaymentSelection(target);
			updateAriaChecked();
			if (errorEl) errorEl.hidden = true;
			updateContinueState();
		}
	});

	// Keyboard handling on labels: Space/Enter selects
	group?.addEventListener('keydown', (e: KeyboardEvent) => {
		const t = e.target as HTMLElement | null;
		const label = t?.closest(
			'label.payment__option--label[role="radio"]',
		) as HTMLLabelElement | null;
		if (!label) return;
		if (e.key === ' ' || e.key === 'Spacebar' || e.key === 'Enter') {
			e.preventDefault();
			selectByLabel(label);
		}
	});

	// Click on labels should also sync ARIA
	group?.addEventListener('click', () => {
		setTimeout(() => updateAriaChecked(), 0);
	});

	// Submit handler
	f.addEventListener('submit', async (e) => {
		e.preventDefault();
		const selected = f.querySelector<HTMLInputElement>('input[name="paymentMethod"]:checked');
		const braintreeSelected = f.querySelector<HTMLInputElement>(
			'#new-payment-method-braintree-card',
		)?.checked;

		if (!selected || (braintreeSelected && !hostedFieldsInit)) {
			if (errorEl) {
				errorEl.hidden = false;
				(errorEl as HTMLElement).focus?.();
			}
			updateContinueState();
			return;
		}

		if (errorEl) errorEl.hidden = true;

		//const submitBtn = form.querySelector('button[type="submit"]') as HTMLElement;
		//if (submitBtn) toggleClass([submitBtn], ['btn-loading']);
		const action = checkoutData.paymentMethod?.action;
		await submitFormAndUpdateStep(f, action);
	});

	// Initial sync
	const initiallyChecked = f.querySelector<HTMLInputElement>(
		'input[name="paymentMethod"]:checked',
	);
	if (initiallyChecked) {
		handlePaymentSelection(initiallyChecked);
	} else {
		braintreeWrapper?.setAttribute('hidden', 'true');
		continueBtn?.removeAttribute('hidden');
	}

	updateAriaChecked();
	updateContinueState();

	registerStepFocusCallback('paymentMethod', () => {
		initialiseBillingSection();
		billingSubmitListener();
		attachBillingToggle();
		showBillingAddress();

		// Hide address sections if the address has been used for a previous delivery.
		const hideAddresses =
			checkoutData.paymentMethod.fields['paymentMethod.address.usedAsDelivery'];
		if (hideAddresses === 'true') {
			const deliverySummary = document.querySelector('.checkout__step-summary');
			if (deliverySummary) deliverySummary.setAttribute('hidden', 'true');

			const checkoutContent = document.querySelector(
				'.checkout__delivery.checkout__content',
			) as HTMLElement | null;
			if (checkoutContent) {
				checkoutContent.style.border = 'none';
				checkoutContent.style.paddingTop = '0';
			}

			const billingSection = document.querySelector('#payment-method__billing');
			if (billingSection) billingSection.setAttribute('hidden', 'true');
		}
	});
}
